// src/CustomerForm.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import logo from "./dhos-logo.png";

const API_BASE = process.env.REACT_APP_API_BASE;

// --- Helper Functions ---
function qsValue(name) {
  var p = new URLSearchParams(window.location.search);
  return p.get(name) || "";
}

function prettyTime(epochSeconds) {
  if (!epochSeconds) return "-";
  try {
    return new Date(epochSeconds * 1000).toLocaleString([], {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch (e) {
    return String(epochSeconds);
  }
}

async function publicFetch(path, opts) {
  opts = opts || {};
  var method = opts.method || "GET";
  var token = opts.token;
  var body = opts.body;

  var headers = { "Content-Type": "application/json" };
  if (token) headers["X-Access-Token"] = token;

  // Avoid template literals (backticks) to support older parsers
  var url = String(API_BASE || "") + String(path || "");

  var res = await fetch(url, {
    method: method,
    headers: headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  var text = await res.text();
  var data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    data = { raw: text };
  }

  if (!res.ok) {
    var msg =
      (data && (data.error || data.message)) ||
      "HTTP " + res.status + " " + res.statusText;
    throw new Error(msg);
  }
  return data;
}

/**
 * === Customer evidence rules ===
 * Attachments: allow video + raise max to 250MB
 */
var MAX_FILE_SIZE_BYTES = 250 * 1024 * 1024; // 250 MB

// Allowed attachment types (include video/*)
var ALLOWED_MIME_TYPES = new Set([
  "application/pdf",

  // images
  "image/jpeg",
  "image/png",
  "image/webp",

  // basic text/docs
  "text/plain",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
  "application/msword", // doc
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
  "application/vnd.ms-excel", // xls
]);

function isAllowedAttachmentType(file) {
  var t = String((file && file.type) || "").toLowerCase();
  if (!t) return true; // some browsers may provide empty mime; allow and let backend decide
  if (t.indexOf("video/") === 0) return true;
  return ALLOWED_MIME_TYPES.has(t);
}

// Editor image rules
var EDITOR_IMAGE_MAX_BYTES = 50 * 1024 * 1024; // 50MB per pasted image
var EDITOR_ALLOWED_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

function formatBytes(bytes) {
  if (bytes == null) return "-";
  var units = ["B", "KB", "MB", "GB"];
  var b = Number(bytes);
  var i = 0;
  while (b >= 1024 && i < units.length - 1) {
    b = b / 1024;
    i++;
  }
  var fixed = i === 0 ? 0 : 1;
  return String(b.toFixed(fixed)) + " " + units[i];
}

function validateSelectedFile(f) {
  if (!f) return "Please choose a file.";
  if (f.size > MAX_FILE_SIZE_BYTES) {
    return "File is too large. Max allowed is " + formatBytes(MAX_FILE_SIZE_BYTES) + ".";
  }
  if (!isAllowedAttachmentType(f)) {
    return (
      "Unsupported file type (" +
      (f.type || "unknown") +
      "). Allowed: PDF, images (JPG/PNG/WebP), video, TXT, DOC/DOCX, XLS/XLSX."
    );
  }
  return "";
}

function isSubmissionLocked(incident) {
  var st = String((incident && incident.status) || "").toUpperCase();
  return (
    Boolean(incident && incident.customerSubmittedAt) ||
    ["CUSTOMER_SUBMITTED", "CLOSED", "ARCHIVED", "DELETED"].indexOf(st) !== -1
  );
}

function parseSubmittedAt(inc) {
  if (!inc) return null;

  var v = inc.customerSubmittedAt || inc.submittedAt || null;
  if (!v) return null;

  // epoch seconds
  if (typeof v === "number") return new Date(v * 1000);

  // epoch string
  if (typeof v === "string" && /^[0-9]+$/.test(v)) return new Date(Number(v) * 1000);

  // ISO string
  var d = new Date(v);
  if (!isNaN(d.getTime())) return d;

  return null;
}

// Basic email validator
function isValidEmail(email) {
  var v = String(email || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

// Escape HTML (no replaceAll; safer)
function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Convert HTML to readable plain text (for legacy/validation)
function htmlToPlainText(html) {
  if (!html) return "";
  var tmp = document.createElement("div");
  tmp.innerHTML = html;
  return String(tmp.textContent || tmp.innerText || "").trim();
}

// Insert HTML at caret in contentEditable
function insertHtmlAtCaret(html) {
  var sel = window.getSelection();
  var range;

  if (sel && sel.getRangeAt && sel.rangeCount) {
    range = sel.getRangeAt(0);
    range.deleteContents();

    var el = document.createElement("div");
    el.innerHTML = html;
    var frag = document.createDocumentFragment();
    var node;
    var lastNode = null;

    // eslint-disable-next-line no-cond-assign
    while ((node = el.firstChild)) {
      lastNode = frag.appendChild(node);
    }
    range.insertNode(frag);

    if (lastNode) {
      range = range.cloneRange();
      range.setStartAfter(lastNode);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  } else if (document.execCommand) {
    document.execCommand("insertHTML", false, html);
  }
}

export default function CustomerForm() {
  var incidentId = useMemo(function () {
    return qsValue("incidentId");
  }, []);
  var token = useMemo(function () {
    return qsValue("token");
  }, []);

  var _d = useState(null),
    incident = _d[0],
    setIncident = _d[1];

  var incidentReportId = useMemo(
    function () {
      return (
        (incident &&
          (incident.reportNo ||
            (incident.incidentData && incident.incidentData.reportNo))) ||
        incidentId ||
        ""
      );
    },
    [incident, incidentId]
  );

  var _a = useState(false),
    loading = _a[0],
    setLoading = _a[1];
  var _b = useState(""),
    err = _b[0],
    setErr = _b[1];
  var _c = useState(""),
    ok = _c[0],
    setOk = _c[1];

  // ✅ Attachment-specific alerts (so file-type warnings show near Attachments section)
  var _c2 = useState(""),
    attachmentErr = _c2[0],
    setAttachmentErr = _c2[1];
  var _c3 = useState(""),
    attachmentOk = _c3[0],
    setAttachmentOk = _c3[1];

  // Success page state
  var _e = useState(false),
    submitSuccess = _e[0],
    setSubmitSuccess = _e[1];
  var _f = useState(null),
    submittedAt = _f[0],
    setSubmittedAt = _f[1];

  // Form Fields
  var _g = useState(""),
    reportedBy = _g[0],
    setReportedBy = _g[1];
  var _h = useState(""),
    affiliateEmail = _h[0],
    setAffiliateEmail = _h[1];
  var _i = useState(""),
    customer = _i[0],
    setCustomer = _i[1];
  var _j = useState(""),
    siteLocation = _j[0],
    setSiteLocation = _j[1];
  var _k = useState(""),
    commissioningDate = _k[0],
    setCommissioningDate = _k[1];
  var _l = useState(""),
    incidentDate = _l[0],
    setIncidentDate = _l[1];
  var _m = useState(""),
    controllerModel = _m[0],
    setControllerModel = _m[1];
  var _n = useState(""),
    productSerialNumber = _n[0],
    setProductSerialNumber = _n[1];
  var _o = useState(""),
    softwareVersion = _o[0],
    setSoftwareVersion = _o[1];

  // Rich description
  var editorRef = useRef(null);
  var _p = useState(""),
    descriptionHtml = _p[0],
    setDescriptionHtml = _p[1];
  var _q = useState(false),
    editorUploading = _q[0],
    setEditorUploading = _q[1];

  // attachments
  var _r = useState([]),
    attachments = _r[0],
    setAttachments = _r[1];
  var _s = useState(null),
    file = _s[0],
    setFile = _s[1];
  var _t = useState(false),
    uploading = _t[0],
    setUploading = _t[1];

  // upload progress for large files (videos)
  var _u = useState(0),
    uploadProgress = _u[0],
    setUploadProgress = _u[1];
  var _v = useState({ loaded: 0, total: 0 }),
    uploadBytes = _v[0],
    setUploadBytes = _v[1];

  // allow resetting file input so selecting same file triggers onChange again
  var fileInputRef = useRef(null);

  var canUse = Boolean(API_BASE && incidentId && token);
  var locked = isSubmissionLocked(incident);

  // Keep editor DOM synced when we programmatically set descriptionHtml
  useEffect(
    function () {
      if (editorRef.current && editorRef.current.innerHTML !== (descriptionHtml || "")) {
        editorRef.current.innerHTML = descriptionHtml || "";
      }
    },
    [descriptionHtml]
  );

  // XHR helper for S3 PUT with progress events
  function putToS3WithProgress(uploadUrl, fileObj, contentType, onProgress) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl, true);

      if (contentType) xhr.setRequestHeader("Content-Type", contentType);

      xhr.upload.onprogress = function (evt) {
        if (evt.lengthComputable && typeof onProgress === "function") {
          onProgress(evt.loaded, evt.total);
        }
      };

      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          var etag = String(
            xhr.getResponseHeader("ETag") || xhr.getResponseHeader("etag") || ""
          ).replace(/"/g, "");
          resolve({ etag: etag });
        } else {
          reject(new Error("Upload failed: HTTP " + xhr.status + " " + (xhr.responseText || "")));
        }
      };

      xhr.onerror = function () {
        reject(new Error("Upload failed: network error."));
      };
      xhr.onabort = function () {
        reject(new Error("Upload aborted."));
      };

      xhr.send(fileObj);
    });
  }

  async function loadAttachmentsOnly() {
    var att = await publicFetch("/public/incident/" + incidentId + "/attachments", {
      method: "GET",
      token: token,
    });
    var list = att.items || att.attachments || [];
    setAttachments(Array.isArray(list) ? list : []);
  }

  // ✅ silent refresh: refresh attachments without touching loading/err/ok
  async function silentRefreshAttachments() {
    try {
      var att = await publicFetch("/public/incident/" + incidentId + "/attachments", {
        method: "GET",
        token: token,
      });
      var list = att.items || att.attachments || [];
      setAttachments(Array.isArray(list) ? list : []);
    } catch (e) {
      // silent
    }
  }

  function setEditorHtmlSafely(html) {
    setDescriptionHtml(html || "");
    if (editorRef.current) editorRef.current.innerHTML = html || "";
  }

  async function loadAll() {
    if (!canUse) return;
    setLoading(true);
    setErr("");
    setOk("");
    try {
      var inc = await publicFetch("/public/incident/" + incidentId, {
        method: "GET",
        token: token,
      });

      setIncident(inc);

      // ✅ If already submitted, show the same success page as after submit
      if (isSubmissionLocked(inc)) {
        setSubmittedAt(parseSubmittedAt(inc) || new Date());
        setSubmitSuccess(true);
        return; // stop here; no need to render form UI
      }

      await loadAttachmentsOnly();

      var prev = (inc && inc.customerResponse) || {};

      if (!reportedBy) setReportedBy(prev.reportedBy || prev.name || "");
      if (!affiliateEmail) setAffiliateEmail(prev.affiliateEmail || "");
      if (!customer) setCustomer(prev.customer || prev.customerName || "");
      if (!siteLocation) setSiteLocation(prev.siteLocation || prev.site || "");
      if (!commissioningDate) setCommissioningDate(prev.commissioningDate || "");
      if (!incidentDate) setIncidentDate(prev.incidentDate || "");
      if (!controllerModel) setControllerModel(prev.controllerModel || "");
      if (!productSerialNumber)
        setProductSerialNumber(prev.productSerialNumber || prev.serialNumber || "");
      if (!softwareVersion) setSoftwareVersion(prev.softwareVersion || "");

      var prevHtml = prev.descriptionHtml || prev.descriptionHTML || prev.descriptionRich || "";
      var prevText = prev.description || prev.message || "";

      if (!descriptionHtml) {
        if (prevHtml) {
          setEditorHtmlSafely(prevHtml);
        } else if (prevText) {
          setEditorHtmlSafely("<p>" + escapeHtml(prevText) + "</p>");
        } else {
          setEditorHtmlSafely("");
        }
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(
    function () {
      if (canUse) loadAll();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [canUse]
  );

  // ✅ Poll attachments silently while link is open (no refresh button needed)
  useEffect(
    function () {
      if (!canUse) return;
      if (locked) return;

      var t = setInterval(function () {
        silentRefreshAttachments();
      }, 8000);

      return function () {
        clearInterval(t);
      };
    },
    [canUse, locked, incidentId, token]
  );

  function handleEditorInput() {
    var html = (editorRef.current && editorRef.current.innerHTML) || "";
    setDescriptionHtml(html);
  }

  // ====== PASTE IMAGE FLOW (Public) ======
  async function presignEditorImagePublic(fileObj) {
    return await publicFetch("/public/incident/" + incidentId + "/editor-images/presign", {
      method: "POST",
      token: token,
      body: {
        filename: fileObj.name,
        contentType: fileObj.type || "application/octet-stream",
        size: fileObj.size,
      },
    });
  }

  async function confirmEditorImagePublic(args) {
    args = args || {};
    return await publicFetch("/public/incident/" + incidentId + "/editor-images/confirm", {
      method: "POST",
      token: token,
      body: {
        imageId: args.imageId || undefined,
        attachmentId: args.attachmentId || undefined,
        etag: args.etag || undefined,
        s3Key: args.s3Key || undefined,
      },
    });
  }

  async function uploadEditorImageAndInsert(fileObj) {
    if (!fileObj) return;

    if (!EDITOR_ALLOWED_IMAGE_TYPES.has(fileObj.type)) {
      throw new Error("Unsupported image type (" + fileObj.type + "). Allowed: JPG/PNG/WebP.");
    }
    if (fileObj.size > EDITOR_IMAGE_MAX_BYTES) {
      throw new Error("Pasted image too large. Max " + formatBytes(EDITOR_IMAGE_MAX_BYTES) + ".");
    }

    var presign = await presignEditorImagePublic(fileObj);
    var uploadUrl = presign && presign.uploadUrl;
    var contentType = (presign && presign.contentType) || fileObj.type || "application/octet-stream";

    var imageId =
      (presign && (presign.imageId || presign.editorImageId || presign.id)) || undefined;
    var attachmentId = (presign && presign.attachmentId) || undefined;

    var s3Key = (presign && (presign.s3Key || presign.key || presign.s3_key)) || undefined;

    if (!uploadUrl) throw new Error("Presign did not return uploadUrl.");
    if (!s3Key)
      throw new Error("Presign did not return s3Key/key. Update presign lambda to return it.");

    var putRes = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: fileObj,
    });

    if (!putRes.ok) {
      var t = "";
      try {
        t = await putRes.text();
      } catch (e) {}
      throw new Error("Image upload failed: HTTP " + putRes.status + " " + t);
    }

    var etagRaw = putRes.headers.get("ETag") || putRes.headers.get("etag");
    var etag = etagRaw ? String(etagRaw).replace(/"/g, "") : undefined;

    var confirmed = await confirmEditorImagePublic({
      imageId: imageId,
      attachmentId: attachmentId,
      etag: etag,
      s3Key: s3Key,
    });

    var url =
      (confirmed &&
        (confirmed.imageUrl ||
          confirmed.publicUrl ||
          confirmed.viewUrl ||
          confirmed.downloadUrl ||
          confirmed.url)) ||
      (presign && presign.publicUrl) ||
      null;

    if (!url) {
      throw new Error(
        "Confirm did not return an image URL (imageUrl/publicUrl/viewUrl/url). Update confirm lambda to return one."
      );
    }

    insertHtmlAtCaret(
      '<div style="margin:10px 0;">' +
        '<img src="' +
        escapeHtml(url) +
        '" alt="' +
        escapeHtml(fileObj.name) +
        '" style="max-width:100%;height:auto;border-radius:8px;border:1px solid #e2e8f0;" />' +
        "</div>"
    );

    handleEditorInput();
  }

  async function onEditorPaste(e) {
    if (locked) return;

    var cb = e.clipboardData;
    if (!cb) return;

    var items = Array.from(cb.items || []);
    var imageItems = items.filter(function (it) {
      return it.kind === "file" && String(it.type || "").indexOf("image/") === 0;
    });

    if (imageItems.length === 0) return;

    e.preventDefault();
    setErr("");
    setOk("");

    try {
      setEditorUploading(true);

      for (var idx = 0; idx < imageItems.length; idx++) {
        var it = imageItems[idx];
        var f = it.getAsFile();
        if (!f) continue;
        await uploadEditorImageAndInsert(f);
      }

      setOk("Image pasted ✅");
    } catch (ex) {
      setErr(ex.message || String(ex));
    } finally {
      setEditorUploading(false);
    }
  }

  async function submitResponse() {
    setErr("");
    setOk("");

    if (!canUse) {
      setErr("Missing configuration or URL parameters.");
      return;
    }
    if (locked) return;

    var plainText = htmlToPlainText(descriptionHtml);

    var payload = {
      reportedBy: String(reportedBy || "").trim(),
      affiliateEmail: String(affiliateEmail || "").trim(),
      customer: String(customer || "").trim(),
      siteLocation: String(siteLocation || "").trim(),
      commissioningDate: commissioningDate,
      incidentDate: incidentDate,
      controllerModel: String(controllerModel || "").trim(),
      productSerialNumber: String(productSerialNumber || "").trim(),
      softwareVersion: String(softwareVersion || "").trim(),
      description: plainText,
      descriptionHtml: descriptionHtml,
    };

    if (
      !payload.reportedBy ||
      !payload.affiliateEmail ||
      !payload.customer ||
      !payload.siteLocation ||
      !payload.incidentDate ||
      !payload.controllerModel ||
      !payload.productSerialNumber ||
      !payload.description
    ) {
      setErr("Please fill in all required fields marked with *.");
      return;
    }

    if (!isValidEmail(payload.affiliateEmail)) {
      setErr(
        "Affiliate Email is not valid. Please enter a valid email address (e.g. name@company.com)."
      );
      return;
    }

    setLoading(true);
    try {
      await publicFetch("/public/incident/" + incidentId + "/submit", {
        method: "POST",
        token: token,
        body: {
          customerResponse: Object.assign({}, payload, {
            name: payload.reportedBy,
            message: payload.description,
          }),
        },
      });

      setSubmitSuccess(true);
      setSubmittedAt(new Date());
      setOk("");
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  // ✅ upload a specific file (for auto-upload)
  async function uploadSpecificFile(fileObj) {
    setAttachmentErr("");
    setAttachmentOk("");

    if (!canUse) {
      setAttachmentErr("Missing configuration or URL parameters.");
      return;
    }
    if (locked) {
      setAttachmentErr("This form has already been submitted. Attachments can’t be changed.");
      return;
    }

    var msg = validateSelectedFile(fileObj);
    if (msg) {
      setAttachmentErr(msg);
      return;
    }

    setUploading(true);
    try {
      var presign = await publicFetch("/public/incident/" + incidentId + "/attachments/presign", {
        method: "POST",
        token: token,
        body: {
          filename: fileObj.name,
          contentType: fileObj.type || "application/octet-stream",
          size: fileObj.size,
        },
      });

      var uploadUrl = presign && presign.uploadUrl;
      var attachmentId = presign && presign.attachmentId;
      var contentType = (presign && presign.contentType) || undefined;

      if (!uploadUrl || !attachmentId)
        throw new Error("Presign response missing uploadUrl/attachmentId.");

      // Reset progress
      setUploadProgress(0);
      setUploadBytes({ loaded: 0, total: fileObj.size || 0 });

      var ct = contentType || fileObj.type || "application/octet-stream";

      var putResult = await putToS3WithProgress(uploadUrl, fileObj, ct, function (loaded, total) {
        setUploadBytes({ loaded: loaded, total: total });
        var pct = total > 0 ? Math.round((loaded / total) * 100) : 0;
        setUploadProgress(pct);
      });

      await publicFetch("/public/incident/" + incidentId + "/attachments/confirm", {
        method: "POST",
        token: token,
        body: {
          attachmentId: attachmentId,
          etag: putResult && putResult.etag,
        },
      });

      setAttachmentOk("File uploaded! ✅");
      setFile(null);
      await loadAttachmentsOnly();
    } catch (e) {
      setAttachmentErr(e.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadBytes({ loaded: 0, total: 0 });

      // allow selecting same file again
      if (fileInputRef.current) {
        try {
          fileInputRef.current.value = "";
        } catch (e) {}
      }
    }
  }

  // keep existing name for any other calls (not used by UI anymore)
  async function uploadAttachment() {
    if (!file) return;
    return uploadSpecificFile(file);
  }

  async function downloadAttachment(a) {
    setAttachmentErr("");
    setAttachmentOk("");
    try {
      var r = await publicFetch("/public/incident/" + incidentId + "/attachments/download-presign", {
        method: "POST",
        token: token,
        body: { attachmentId: a.attachmentId },
      });

      var url = (r && (r.downloadUrl || r.url)) || null;
      if (!url) throw new Error("No downloadUrl returned.");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setAttachmentErr(e.message);
    }
  }

  async function deleteAttachment(a) {
    setAttachmentErr("");
    setAttachmentOk("");

    if (locked) {
      setAttachmentErr("This form has already been submitted. Attachments can’t be deleted.");
      return;
    }

    if (!window.confirm('Delete attachment "' + a.filename + '"?')) return;

    try {
      await publicFetch("/public/incident/" + incidentId + "/attachments/delete", {
        method: "POST",
        token: token,
        body: { attachmentId: a.attachmentId, deleteS3: false },
      });

      setAttachmentOk("Deleted ✅");
      await loadAttachmentsOnly();
    } catch (e) {
      setAttachmentErr(e.message);
    }
  }

  // Always hide deleted
  var visibleAttachments = useMemo(
    function () {
      var list = Array.isArray(attachments) ? attachments : [];
      return list.filter(function (a) {
        return String((a && a.status) || "").toUpperCase() !== "DELETED";
      });
    },
    [attachments]
  );

  // --- Styles ---
  var s = {
    container: {
      maxWidth: 900,
      margin: "16px auto",
      padding: "0 20px",
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      color: "#334155",
    },
    card: {
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: 12,
      padding: 24,
      marginBottom: 24,
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    },
    title: { fontSize: 24, fontWeight: 700, margin: 0, color: "#1e293b" },
    label: {
      display: "block",
      fontSize: 12,
      fontWeight: 600,
      color: "#64748b",
      marginBottom: 6,
      textTransform: "uppercase",
    },
    input: {
      width: "100%",
      padding: "10px",
      borderRadius: 6,
      border: "1px solid #cbd5e1",
      boxSizing: "border-box",
      fontSize: "15px",
    },
    btnPrimary: {
      background: "#2563eb",
      color: "#fff",
      border: "none",
      padding: "10px 20px",
      borderRadius: 6,
      fontWeight: 600,
      cursor: "pointer",
    },

    // ✅ Overview layout
    infoGridOverview: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr auto",
      gap: "16px",
      background: "#f8fafc",
      padding: "20px",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      alignItems: "end",
    },
    infoValueId: {
      fontSize: "15px",
      fontWeight: "700",
      color: "#0f172a",
    },

    infoItem: { display: "flex", flexDirection: "column", gap: "2px" },
    infoLabel: {
      fontSize: "11px",
      fontWeight: "700",
      color: "#64748b",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    infoValue: { fontSize: "15px", fontWeight: "500", color: "#0f172a" },

    alert: function (type) {
      return {
        padding: 16,
        borderRadius: 8,
        marginBottom: 16,
        background: type === "err" ? "#fef2f2" : "#f0fdf4",
        color: type === "err" ? "#991b1b" : "#166534",
        border: "1px solid " + (type === "err" ? "#fecaca" : "#bbf7d0"),
        fontWeight: 500,
      };
    },

    headerBar: { background: "white", overflow: "hidden" },

    headerRowLogo: {
      padding: "2px 0 4px 0",
      display: "flex",
      justifyContent: "flex-start",
      alignItems: "flex-start",
    },

    logo: { width: 180, height: 90, objectFit: "contain", display: "block" },

    headerRowTitle: { padding: "4px 0 10px 0", textAlign: "center" },

    pageTitle: {
      margin: 0,
      fontSize: 22,
      fontWeight: 900,
      color: "#0f172a",
      lineHeight: 1.15,
    },

    topDivider: { height: 1, background: "#e2e8f0", margin: "16px 0" },

    sectionHeader: {
      display: "grid",
      gridTemplateColumns: "1fr auto 1fr",
      alignItems: "center",
      marginBottom: 16,
    },
    sectionTitle: { margin: 0, fontSize: 18, textAlign: "center" },

    note: {
      marginTop: 0,
      marginBottom: 16,
      padding: 12,
      borderRadius: 10,
      background: "#f8fafc",
      border: "1px solid #e2e8f0",
      color: "#475569",
      fontSize: 13,
      fontWeight: 600,
    },

    editorWrap: {
      border: "1px solid #cbd5e1",
      borderRadius: 8,
      overflow: "hidden",
      background: "white",
    },
    editorTopBar: {
      display: "flex",
      gap: 8,
      alignItems: "center",
      padding: "8px 10px",
      borderBottom: "1px solid #e2e8f0",
      background: "#f8fafc",
      fontSize: 12,
      color: "#64748b",
      fontWeight: 600,
    },
    editor: {
      padding: 12,
      minHeight: 180,
      outline: "none",
      fontSize: 15,
      lineHeight: 1.45,
    },
  };

  // Early warnings
  if (!API_BASE) {
    return (
      <div style={s.container}>
        <div style={s.alert("err")}>Missing REACT_APP_API_BASE in your environment.</div>
      </div>
    );
  }

  if (!incidentId || !token) {
    return (
      <div style={s.container}>
        <div style={s.alert("err")}>
          Missing URL parameters. Use:
          <pre
            style={{
              marginTop: 10,
              background: "#fff",
              padding: 12,
              borderRadius: 8,
              border: "1px solid #e2e8f0",
            }}
          >
            {"/customer?incidentId=INC-xxxx&token=xxxx"}
          </pre>
        </div>
      </div>
    );
  }

  // Success screen (also shown when reusing a submitted link)
  if (submitSuccess) {
    return (
      <div style={s.container}>
        <div style={s.card}>
          <h1 style={{ margin: 0, color: "#0f172a" }}>✅ Incident Report Submitted</h1>
          <div style={{ marginTop: 10, color: "#475569", fontSize: 14 }}>
            Thank you. Your response and attachments have been submitted successfully.
          </div>

          <div style={{ marginTop: 16, color: "#64748b", fontSize: 13 }}>
            {submittedAt ? "Submitted: " + submittedAt.toLocaleString() : null}
          </div>

          <div
            style={{
              marginTop: 18,
              padding: 12,
              borderRadius: 12,
              background: "#f8fafc",
              color: "#475569",
              fontSize: 13,
            }}
          >
            You can now close this window.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.container}>
      {/* ✅ OPTION A: ONE combined card = Header + Overview + Details (no separation) */}
      <div style={s.card}>
        {/* Header */}
        <div style={s.headerBar}>
          <div style={s.headerRowLogo}>
            <img src={logo} alt="DHOS" style={s.logo} />
          </div>

          <div style={s.headerRowTitle}>
            <h1 style={s.pageTitle}>DHOS Incident Report Form</h1>
          </div>
        </div>

        {/* Overview */}
        {incident ? (
          <>
            <div style={s.topDivider} />

            <div style={s.sectionHeader}>
              <div />
              <h3 style={s.sectionTitle}>Incident Overview</h3>
              <div />
            </div>

            <div style={s.infoGridOverview}>
              <div style={s.infoItem}>
                <span style={s.infoLabel}>Product</span>
                <span style={s.infoValue}>
                  {(incident.incidentData && incident.incidentData.product) || "—"}
                </span>
              </div>

              <div style={s.infoItem}>
                <span style={s.infoLabel}>Affiliate</span>
                <span style={s.infoValue}>
                  {(incident.incidentData && incident.incidentData.affiliate) || "—"}
                </span>
              </div>

              <div style={{ ...s.infoItem, textAlign: "right" }}>
                <span style={s.infoLabel}>Incident Report ID</span>
                <span style={s.infoValueId ? s.infoValueId : s.infoValue}>
                  {incidentReportId || "—"}
                </span>
              </div>

              <div style={{ ...s.infoItem, gridColumn: "1 / -1" }}>
                <span style={s.infoLabel}>Subject</span>
                <span style={{ ...s.infoValue, wordBreak: "break-word", overflowWrap: "anywhere" }}>
                  {(incident.incidentData && incident.incidentData.subject) || "—"}
                </span>
              </div>
            </div>
          </>
        ) : null}

        {/* Details */}
        <div style={s.topDivider} />

        <div style={s.sectionHeader}>
          <div />
          <h3 style={s.sectionTitle}>Incident Details</h3>
          <div />
        </div>

        <div style={s.note}>
          Please complete all required fields marked with <b>*</b>.
        </div>

        {err ? <div style={s.alert("err")}>{err}</div> : null}
        {ok ? <div style={s.alert("ok")}>{ok}</div> : null}
        {loading ? <div style={{ marginBottom: 16, color: "#64748b" }}>Loading...</div> : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 20,
          }}
        >
          <div>
            <label style={s.label}>Reported By *</label>
            <input
              style={s.input}
              value={reportedBy}
              onChange={(e) => setReportedBy(e.target.value)}
              disabled={locked}
            />
          </div>

          <div>
            <label style={s.label}>Affiliate Email *</label>
            <input
              type="email"
              style={s.input}
              value={affiliateEmail}
              onChange={(e) => setAffiliateEmail(e.target.value)}
              disabled={locked}
              placeholder="e.g. affiliate@example.com"
            />
          </div>

          <div>
            <label style={s.label}>Customer *</label>
            <input
              style={s.input}
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
              disabled={locked}
            />
          </div>

          <div>
            <label style={s.label}>Site Location *</label>
            <input
              style={s.input}
              value={siteLocation}
              onChange={(e) => setSiteLocation(e.target.value)}
              disabled={locked}
            />
          </div>

          <div>
            <label style={s.label}>Incident Date *</label>
            <input
              type="date"
              style={s.input}
              value={incidentDate}
              onChange={(e) => setIncidentDate(e.target.value)}
              disabled={locked}
            />
          </div>

          <div>
            <label style={s.label}>Product Model *</label>
            <input
              style={s.input}
              value={controllerModel}
              onChange={(e) => setControllerModel(e.target.value)}
              disabled={locked}
            />
          </div>

          <div>
            <label style={s.label}>Serial Number *</label>
            <input
              style={s.input}
              value={productSerialNumber}
              onChange={(e) => setProductSerialNumber(e.target.value)}
              disabled={locked}
            />
          </div>

          <div>
            <label style={s.label}>Software Version</label>
            <input
              style={s.input}
              value={softwareVersion}
              onChange={(e) => setSoftwareVersion(e.target.value)}
              disabled={locked}
            />
          </div>

          <div>
            <label style={s.label}>Commissioning Date</label>
            <input
              type="date"
              style={s.input}
              value={commissioningDate}
              onChange={(e) => setCommissioningDate(e.target.value)}
              disabled={locked}
            />
          </div>
        </div>

        <div style={{ marginTop: 20 }}>
          <label style={s.label}>Detailed Description *</label>

          <div style={s.editorWrap}>
            <div style={s.editorTopBar}>
              <span>Tip: Paste images (Ctrl+V). They’ll upload automatically.</span>
              {editorUploading ? (
                <span style={{ marginLeft: "auto", color: "#0f766e" }}>Uploading image…</span>
              ) : null}
            </div>

            <div
              ref={editorRef}
              contentEditable={!locked}
              suppressContentEditableWarning
              onInput={handleEditorInput}
              onPaste={onEditorPaste}
              style={{
                ...s.editor,
                background: locked ? "#f8fafc" : "white",
                color: locked ? "#64748b" : "#0f172a",
              }}
              data-placeholder="Please provide technical details of the incident..."
            />
          </div>

          <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
            Pasted images: max <b>{formatBytes(EDITOR_IMAGE_MAX_BYTES)}</b>, allowed: JPG/PNG/WebP.
          </div>
        </div>

        {locked ? (
          <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
            If the customer needs to update anything, the agent/admin should <b>reopen the link</b>{" "}
            (new token/expiry).
          </div>
        ) : null}
      </div>

      {/* 3. Attachments */}
      <div style={s.card}>
        {/* ✅ Attachment alerts appear here (near the upload control) */}
        {attachmentErr ? <div style={s.alert("err")}>{attachmentErr}</div> : null}
        {attachmentOk ? <div style={s.alert("ok")}>{attachmentOk}</div> : null}

        <h3 style={{ marginTop: 0, marginBottom: 5 }}>Attachments & Evidence</h3>
        <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>
          Max file size: <b>{formatBytes(MAX_FILE_SIZE_BYTES)}</b>. Allowed: PDF, Images (JPG/PNG/WebP),
          Video, TXT, Word (DOC/DOCX), Excel (XLS/XLSX).
        </p>

        {!locked ? (
          <div style={{ background: "#f8fafc", padding: 16, borderRadius: 8, marginBottom: 20 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf,video/*,text/plain,.doc,.docx,.xls,.xlsx"
                disabled={uploading}
                onChange={(e) => {
                  // ✅ auto-upload on selection
                  var f = (e.target.files && e.target.files[0]) || null;

                  // clear old attachment messages whenever user picks a new file
                  setAttachmentErr("");
                  setAttachmentOk("");

                  setFile(f);

                  if (!f) return;

                  var msg = validateSelectedFile(f);
                  if (msg) {
                    setAttachmentErr(msg);
                    // reset input so picking same file again still triggers change
                    try {
                      e.target.value = "";
                    } catch (ex) {}
                    return;
                  }

                  // valid -> upload immediately
                  uploadSpecificFile(f);
                }}
                style={{ flex: 1 }}
              />

              {/* ✅ Removed Upload button (auto-upload now) */}
              <div style={{ fontSize: 12, color: "#64748b", minWidth: 140, textAlign: "right" }}>
                {uploading ? "Uploading…" : "Select a file to upload"}
              </div>
            </div>

            {uploading ? (
              <div style={{ marginTop: 10, width: "100%" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 12,
                    color: "#64748b",
                    marginBottom: 6,
                  }}
                >
                  <span>{"Uploading… " + uploadProgress + "%"}</span>
                  <span>
                    {uploadBytes.total
                      ? formatBytes(uploadBytes.loaded) + " / " + formatBytes(uploadBytes.total)
                      : null}
                  </span>
                </div>

                <div
                  style={{
                    height: 10,
                    background: "#e2e8f0",
                    borderRadius: 999,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: String(Math.min(100, Math.max(0, uploadProgress))) + "%",
                      background: "#2563eb",
                      transition: "width 120ms linear",
                    }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 14 }}>
            {"Files (" + visibleAttachments.length + ")"}
          </span>
        </div>

        {visibleAttachments.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "30px",
              border: "2px dashed #e2e8f0",
              borderRadius: 8,
              color: "#94a3b8",
            }}
          >
            No files uploaded.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table width="100%" style={{ borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr
                  style={{
                    borderBottom: "2px solid #f1f5f9",
                    textAlign: "left",
                    color: "#64748b",
                  }}
                >
                  <th style={{ padding: 12 }}>File Details</th>
                  <th style={{ padding: 12 }}>Status</th>
                  <th style={{ padding: 12, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleAttachments.map(function (a) {
                  var isDeleted = String((a && a.status) || "").toUpperCase() === "DELETED";

                  return (
                    <tr
                      key={a.attachmentId}
                      style={{
                        borderBottom: "1px solid #f1f5f9",
                        opacity: isDeleted ? 0.6 : 1,
                      }}
                    >
                      <td style={{ padding: 12 }}>
                        <div style={{ fontWeight: 600, color: "#1e293b" }}>{a.filename}</div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>
                          {formatBytes(a.size) + " · " + prettyTime(a.uploadedAt)}
                        </div>
                        <div style={{ fontSize: 12, color: "#94a3b8" }}>{a.attachmentId}</div>
                      </td>

                      <td style={{ padding: 12 }}>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{a.status || "-"}</span>
                      </td>

                      <td style={{ padding: 12, textAlign: "right" }}>
                        <button
                          onClick={() => downloadAttachment(a)}
                          style={{
                            background: "#f1f5f9",
                            color: "#475569",
                            border: "1px solid #e2e8f0",
                            padding: "6px 12px",
                            borderRadius: 6,
                            cursor: "pointer",
                            fontSize: "13px",
                            marginRight: 6,
                          }}
                          disabled={isDeleted}
                        >
                          Download
                        </button>

                        {!locked && !isDeleted ? (
                          <button
                            onClick={() => deleteAttachment(a)}
                            style={{
                              background: "#f1f5f9",
                              color: "#dc2626",
                              border: "1px solid #fecaca",
                              padding: "6px 12px",
                              borderRadius: 6,
                              cursor: "pointer",
                              fontSize: "13px",
                            }}
                            disabled={uploading}
                          >
                            Delete
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {locked ? (
          <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
            Attachments are locked after submission.
          </div>
        ) : null}
      </div>

      {/* Bottom submit button */}
      <div style={s.card}>
        <div style={{ textAlign: "center" }}>
          <button
            onClick={submitResponse}
            style={{
              ...s.btnPrimary,
              background: locked ? "#94a3b8" : "#2563eb",
              minWidth: 220,
            }}
            disabled={locked || loading || editorUploading}
          >
            {loading ? "Submitting..." : editorUploading ? "Uploading..." : "Submit Response"}
          </button>

          <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
            Once submitted, this link can’t be reused to edit. Please contact DHOS Support if changes
            are needed.
          </div>
        </div>
      </div>
    </div>
  );
}
